const DEFAULT_PRODUCTION_CMS_API_URL = 'https://cms.fpep.me/api/public';
const DEFAULT_DEVELOPMENT_CMS_API_URL = 'http://localhost:3001/api/public';
const DEFAULT_TIMEOUT_MS = 8000;
const DEFAULT_RETRY_COUNT = 1;
const TRANSIENT_CMS_STATUS_CODES = new Set([403, 408, 425, 429, 500, 502, 503, 504]);

const trimTrailingSlash = (value = '') => String(value).replace(/\/+$/, '');
const trimLeadingSlash = (value = '') => String(value).replace(/^\/+/, '');

const parseBooleanEnv = (value, fallback = true) => {
  if (value === undefined || value === null || value === '') return fallback;

  const normalized = String(value).trim().toLowerCase();

  if (['1', 'true', 'yes', 'da', 'on'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'ne', 'off'].includes(normalized)) return false;

  return fallback;
};

const parsePositiveIntegerEnv = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);

  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;

  return parsed;
};

const parseNonNegativeIntegerEnv = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);

  if (!Number.isFinite(parsed) || parsed < 0) return fallback;

  return Math.min(parsed, 3);
};

const getDefaultCmsApiUrl = () => {
  return process.env.NODE_ENV === 'production'
    ? DEFAULT_PRODUCTION_CMS_API_URL
    : DEFAULT_DEVELOPMENT_CMS_API_URL;
};

export const cmsConfig = {
  apiUrl: trimTrailingSlash(process.env.REACT_APP_CMS_API_URL || getDefaultCmsApiUrl()),
  enabled: parseBooleanEnv(process.env.REACT_APP_CMS_ENABLED, true),
  timeoutMs: parsePositiveIntegerEnv(
    process.env.REACT_APP_CMS_TIMEOUT_MS,
    DEFAULT_TIMEOUT_MS,
  ),
  retryCount: parseNonNegativeIntegerEnv(
    process.env.REACT_APP_CMS_RETRY_COUNT,
    DEFAULT_RETRY_COUNT,
  ),
};

const getCmsOrigin = () => {
  try {
    const url = new URL(cmsConfig.apiUrl);
    return url.origin;
  } catch (_error) {
    return '';
  }
};

export class CmsApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = 'CmsApiError';
    this.status = options.status || null;
    this.code = options.code || 'CMS_API_ERROR';
    this.details = options.details || null;
    this.cause = options.cause || null;
  }
}

const buildQueryString = (query = {}) => {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, String(value));
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

const buildCmsUrl = (path, query, attempt = 0) => {
  const cleanPath = trimLeadingSlash(path);
  const requestQuery = attempt > 0
    ? { ...query, __fpep_retry: `${Date.now()}-${attempt}` }
    : query;

  return `${cmsConfig.apiUrl}/${cleanPath}${buildQueryString(requestQuery)}`;
};

const parseCmsResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';

  if (!contentType.includes('application/json')) {
    const text = await response.text();

    if (!response.ok) {
      throw new CmsApiError('CMS API request failed.', {
        status: response.status,
        code: 'CMS_NON_JSON_ERROR',
        details: text,
      });
    }

    return { data: text, meta: null };
  }

  const payload = await response.json();

  if (!response.ok || payload?.success === false) {
    const apiError = payload?.error || payload || {};

    throw new CmsApiError(apiError?.message || payload?.message || 'CMS API request failed.', {
      status: response.status,
      code: apiError?.code || payload?.code || 'CMS_API_ERROR',
      details: apiError?.details || payload?.details || payload,
    });
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'data')) {
    return {
      data: payload.data,
      meta: payload.meta || null,
    };
  }

  return {
    data: payload,
    meta: null,
  };
};

const normalizeCmsRequestError = (error) => {
  if (error instanceof CmsApiError) return error;

  if (error?.name === 'AbortError') {
    return new CmsApiError('CMS API request timed out.', {
      code: 'CMS_TIMEOUT',
      cause: error,
    });
  }

  return new CmsApiError('CMS API is not reachable.', {
    code: 'CMS_NETWORK_ERROR',
    cause: error,
  });
};

const shouldRetryCmsRequest = (error, attempt, controller) => {
  if (attempt >= cmsConfig.retryCount || controller.signal.aborted) return false;
  if (error.code === 'CMS_NETWORK_ERROR') return true;

  return TRANSIENT_CMS_STATUS_CODES.has(error.status);
};

export const cmsRequest = async (path, { query, signal, timeoutMs, ...options } = {}) => {
  if (!cmsConfig.enabled) {
    throw new CmsApiError('CMS integration is disabled.', {
      code: 'CMS_DISABLED',
    });
  }

  if (!cmsConfig.apiUrl) {
    throw new CmsApiError('CMS API URL is not configured.', {
      code: 'CMS_URL_MISSING',
    });
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    timeoutMs || cmsConfig.timeoutMs,
  );

  const abortListener = () => controller.abort();

  if (signal) {
    if (signal.aborted) controller.abort();
    signal.addEventListener('abort', abortListener, { once: true });
  }

  try {
    for (let attempt = 0; attempt <= cmsConfig.retryCount; attempt += 1) {
      try {
        const response = await fetch(buildCmsUrl(path, query, attempt), {
          ...options,
          method: 'GET',
          headers: {
            Accept: 'application/json',
            ...(options.headers || {}),
          },
          signal: controller.signal,
          ...(attempt > 0 ? { cache: 'no-store' } : {}),
        });

        return await parseCmsResponse(response);
      } catch (error) {
        const normalizedError = normalizeCmsRequestError(error);

        if (!shouldRetryCmsRequest(normalizedError, attempt, controller)) {
          throw normalizedError;
        }
      }
    }

    throw new CmsApiError('CMS API is not reachable.', {
      code: 'CMS_NETWORK_ERROR',
    });
  } finally {
    window.clearTimeout(timeout);

    if (signal) {
      signal.removeEventListener('abort', abortListener);
    }
  }
};

export const fetchCmsNews = async ({ page = 1, limit = 9, category, featured, search, locale, signal } = {}) => {
  return cmsRequest('/news', {
    query: {
      page,
      limit,
      category,
      featured,
      search,
      locale,
    },
    signal,
  });
};

export const fetchCmsNewsBySlug = async (slug, { locale, signal } = {}) => {
  return cmsRequest(`/news/${encodeURIComponent(slug)}`, {
    query: { locale },
    signal,
  });
};

export const fetchCmsCategories = async ({ locale, signal } = {}) => {
  return cmsRequest('/categories', {
    query: { locale },
    signal,
  });
};

export const fetchLatestCmsNews = async ({ limit = 5, locale, signal } = {}) => {
  return fetchCmsNews({ page: 1, limit, locale, signal });
};

export const fetchFeaturedCmsNews = async ({ limit = 3, locale, signal } = {}) => {
  return fetchCmsNews({ page: 1, limit, featured: true, locale, signal });
};

export const fetchCmsSettings = async ({ signal } = {}) => {
  return cmsRequest('/settings', { signal });
};

export const fetchCmsPopup = async ({ locale, signal } = {}) => {
  return cmsRequest('/popup', {
    query: { locale },
    signal,
    cache: 'no-store',
  });
};


export const fetchCmsGalleryAlbums = async ({
  page = 1,
  limit = 60,
  category,
  featured,
  search,
  locale,
  signal,
} = {}) => {
  return cmsRequest('/gallery/albums', {
    query: {
      page,
      limit,
      category,
      featured,
      search,
      locale,
    },
    signal,
  });
};

export const fetchCmsGalleryAlbumBySlug = async (slug, { locale, signal } = {}) => {
  return cmsRequest(`/gallery/albums/${encodeURIComponent(slug)}`, {
    query: { locale },
    signal,
  });
};

export const fetchCmsGalleryCategories = async ({ locale, signal } = {}) => {
  return cmsRequest('/gallery/categories', {
    query: { locale },
    signal,
  });
};

export const fetchCmsErasmusCalls = async ({
  page = 1,
  limit = 60,
  partner,
  callType,
  mobilityType,
  targetGroup,
  featured,
  search,
  locale,
  signal,
} = {}) => {
  return cmsRequest('/erasmus/calls', {
    query: {
      page,
      limit,
      partner,
      callType,
      mobilityType,
      targetGroup,
      featured,
      search,
      locale,
    },
    signal,
  });
};

export const fetchCmsErasmusCallBySlug = async (slug, { locale, signal } = {}) => {
  return cmsRequest(`/erasmus/calls/${encodeURIComponent(slug)}`, {
    query: { locale },
    signal,
  });
};

export const fetchCmsErasmusPartners = async ({ locale, signal } = {}) => {
  return cmsRequest('/erasmus/partners', {
    query: { locale },
    signal,
  });
};

const formatDateForLegacyNews = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}.${month}.${year}.`;
};

const stripHtml = (value = '') => {
  const container = document.createElement('div');
  container.innerHTML = String(value || '');
  return container.textContent.replace(/\s+/g, ' ').trim();
};

const isAbsoluteAssetUrl = (value = '') => /^https?:\/\//i.test(String(value || ''));
const isSpecialAssetUrl = (value = '') => /^(data:|blob:)/i.test(String(value || ''));
const stripQueryHash = (value = '') => String(value || '').trim().split(/[?#]/)[0];
const isBareAssetFilename = (value = '') => {
  const normalized = stripQueryHash(value);
  return Boolean(normalized) && !normalized.includes('/') && !normalized.startsWith('.') && !/^[a-zA-Z]:[\\/]/.test(normalized);
};

const normalizeCmsAssetPath = (url) => {
  if (!url) return '';

  const value = String(url).trim().replace(/\\+/g, '/');
  if (!value) return '';

  if (isAbsoluteAssetUrl(value) || isSpecialAssetUrl(value)) return value;
  if (/^[a-zA-Z]:[\\/]/.test(value)) return '';
  if (value.startsWith('/')) return value;
  if (/^uploads\//i.test(value)) return `/${value.replace(/^\/+/, '')}`;
  if (isBareAssetFilename(value)) return '';

  // Legacy CMS imports sometimes expose the storage path without /uploads.
  // Example: "2026/06/some-image/original.webp".
  if (value.includes('/')) return `/uploads/${value.replace(/^\/+/, '')}`;

  return '';
};


const isBrokenRootCmsMediaUrl = (value = '') => {
  if (!isAbsoluteAssetUrl(value)) return false;

  try {
    const url = new URL(value);
    const cmsOrigin = getCmsOrigin();
    const segments = url.pathname.split('/').filter(Boolean);
    if (!cmsOrigin || url.origin !== cmsOrigin || segments.length !== 1) return false;
    return /\.(webp|png|jpe?g|gif|pdf|docx?|xlsx?|pptx?|zip|rar)$/i.test(segments[0]);
  } catch (_error) {
    return false;
  }
};

export const resolveCmsAssetUrl = (url) => {
  const normalized = normalizeCmsAssetPath(url);
  if (!normalized) return '';

  if (isBrokenRootCmsMediaUrl(normalized)) return '';

  if (isAbsoluteAssetUrl(normalized) || isSpecialAssetUrl(normalized)) {
    return normalized;
  }

  const origin = getCmsOrigin();
  if (!origin) return normalized;

  const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
  return `${origin}${path}`;
};

const MEDIA_VARIANT_ORDER = ['medium', 'thumb', 'original'];

const getVariantCandidate = (media, variantKey) => {
  const variant = media?.variants?.[variantKey];
  if (!variant) return '';

  if (typeof variant === 'string') {
    return normalizeCmsAssetPath(variant);
  }

  if (variant.storagePath) {
    const storagePath = String(variant.storagePath).replace(/^\/+/, '');
    return `/uploads/${storagePath}`;
  }

  const candidate =
    variant.absoluteUrl ||
    variant.url ||
    variant.publicUrl ||
    variant.src ||
    variant.path ||
    '';

  return normalizeCmsAssetPath(candidate);
};

const uniqueResolvedUrls = (values = []) => {
  const seen = new Set();

  return values
    .map((value) => resolveCmsAssetUrl(value))
    .filter(Boolean)
    .filter((value) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

export const getCmsMediaCandidateUrls = (media, preferredVariants = ['medium', 'thumb', 'original']) => {
  if (!media) return [];

  const orderedVariants = [
    ...(Array.isArray(preferredVariants) ? preferredVariants : [preferredVariants]),
    ...MEDIA_VARIANT_ORDER,
  ].filter(Boolean);

  return uniqueResolvedUrls([
    ...orderedVariants.map((variantKey) => getVariantCandidate(media, variantKey)),
    media.absoluteUrl,
    media.url,
    media.publicUrl,
  ]);
};

export const getCmsMediaUrl = (media, preferredVariant = 'medium') => {
  return getCmsMediaCandidateUrls(media, [preferredVariant])[0] || '';
};

export const getCmsPostImage = (post, preferredVariant = 'medium') => {
  return getCmsMediaUrl(post?.coverImage, preferredVariant);
};

const isCmsAssetPath = (value = '') => Boolean(normalizeCmsAssetPath(value));

const resolveCmsSrcSet = (srcSet = '') => {
  return String(srcSet || '')
    .split(',')
    .map((entry) => {
      const trimmed = entry.trim();
      if (!trimmed) return '';

      const [url, ...descriptorParts] = trimmed.split(/\s+/);
      const resolvedUrl = isCmsAssetPath(url) ? resolveCmsAssetUrl(url) : url;
      return [resolvedUrl, ...descriptorParts].filter(Boolean).join(' ');
    })
    .filter(Boolean)
    .join(', ');
};

export const resolveCmsHtmlAssetUrls = (html = '') => {
  const rawHtml = String(html || '').trim();

  if (!rawHtml || typeof document === 'undefined') return rawHtml;

  const template = document.createElement('template');
  template.innerHTML = rawHtml;

  template.content.querySelectorAll('[src]').forEach((node) => {
    const value = node.getAttribute('src');
    if (isCmsAssetPath(value)) {
      node.setAttribute('src', resolveCmsAssetUrl(value));
    }
  });

  template.content.querySelectorAll('[href]').forEach((node) => {
    const value = node.getAttribute('href');
    if (isCmsAssetPath(value)) {
      node.setAttribute('href', resolveCmsAssetUrl(value));
    }
  });

  template.content.querySelectorAll('[srcset]').forEach((node) => {
    const value = node.getAttribute('srcset');
    if (value && String(value).split(',').some((entry) => isCmsAssetPath(entry.trim().split(/\s+/)[0]))) {
      node.setAttribute('srcset', resolveCmsSrcSet(value));
    }
  });

  return template.innerHTML;
};

export const getCmsPostDescription = (post, maxLength = 200) => {
  const rawDescription =
    post?.excerpt || post?.seo?.description || post?.content?.textPreview || stripHtml(post?.content?.html);

  const normalized = String(rawDescription || '').replace(/\s+/g, ' ').trim();

  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trim()}…`;
};

export const getCmsPostPath = (post) => {
  if (!post) return '/novosti';

  const slug = post.slug || post.attributes?.Slug;
  const legacyId = post.legacy?.id || null;

  if (legacyId && slug) return `/novosti/${legacyId}-${slug}`;
  if (slug) return `/novosti/${slug}`;

  return '/novosti';
};

export const normalizeCmsPostForLegacyView = (post) => {
  if (!post) return null;

  const gallery = Array.isArray(post.gallery) ? post.gallery : [];
  const legacyId = post.legacy?.id || post.id;
  const description = getCmsPostDescription(post, 2000);
  const coverImageCandidates = getCmsMediaCandidateUrls(post?.coverImage, ['medium', 'thumb', 'original']);
  const coverImage = coverImageCandidates[0] || '';

  return {
    id: legacyId,
    cmsId: post.id,
    source: 'cms',
    routePath: getCmsPostPath(post),
    attributes: {
      Title: post.title || '',
      Description: description,
      Date: formatDateForLegacyNews(post.publishedAt),
      Image: coverImage,
      ImageCandidates: coverImageCandidates,
      Slug: post.slug || '',
      Gallery: {
        data: gallery.map((item) => getCmsMediaCandidateUrls(item, ['medium', 'thumb', 'original'])).filter((candidates) => candidates.length),
      },
    },
    cms: post,
  };
};


const getCmsGalleryImageUrls = (image, preferredVariants = ['medium', 'thumb', 'original']) => {
  return getCmsMediaCandidateUrls(image, preferredVariants);
};


const getCmsGalleryAlbumCoverCandidates = (album) => {
  const coverImageCandidates = getCmsGalleryImageUrls(album?.coverImage, ['medium', 'thumb', 'original']);
  if (coverImageCandidates.length) return coverImageCandidates;

  const firstImage = Array.isArray(album?.images) ? album.images[0] : null;
  return getCmsGalleryImageUrls(firstImage, ['medium', 'thumb', 'original']);
};


export const normalizeCmsGalleryAlbumForLegacyView = (album) => {
  if (!album) return null;

  const images = Array.isArray(album.images) ? album.images : [];
  const imageCandidateGroups = images
    .map((image) => getCmsGalleryImageUrls(image, ['medium', 'thumb', 'original']))
    .filter((candidates) => candidates.length);
  const imageUrls = imageCandidateGroups.map((candidates) => candidates[0]).filter(Boolean);
  const coverImageCandidates = getCmsGalleryAlbumCoverCandidates(album);
  const coverImage = coverImageCandidates[0] || imageUrls[0] || '';
  const ogImageCandidates = getCmsMediaCandidateUrls(album?.ogImage, ['medium', 'original', 'thumb']);
  const seoImageCandidates = ogImageCandidates.length
    ? ogImageCandidates
    : (coverImageCandidates.length ? coverImageCandidates : (imageCandidateGroups[0] || []));
  const legacyId = album.legacy?.id || `cms-gallery-${album.id}`;
  const title = album.title || album.description || 'Galerija';
  const description = album.description || title;
  const totalPhotos = Number(album.imageCount || imageUrls.length || (coverImage ? 1 : 0));

  return {
    id: legacyId,
    cmsId: album.id,
    source: 'cms',
    img: coverImage,
    imgCandidates: coverImageCandidates.length ? coverImageCandidates : (imageCandidateGroups[0] || []),
    category: album.category?.name || 'Galerija',
    categorySlug: album.category?.slug || '',
    description,
    title,
    slug: album.slug || '',
    subImages: imageUrls.filter((url) => url !== coverImage),
    subImageCandidates: imageCandidateGroups.filter((candidates) => candidates[0] !== coverImage),
    totalPhotos: Math.max(totalPhotos, coverImage ? 1 : 0),
    routePath: album.routePath || (album.slug ? `/galerija?album=${encodeURIComponent(album.slug)}` : '/galerija'),
    seoTitle: album?.seo?.title || title,
    seoDescription: album?.seo?.description || description,
    ogImage: seoImageCandidates[0] || coverImage,
    ogImageCandidates: seoImageCandidates,
    hasEnglishContent: Boolean(album?.hasEnglishContent || album?.locale?.available?.en || album?.translations?.en),
    publishedAt: album.publishedAt || null,
    updatedAt: album.updatedAt || null,
    cms: album,
  };
};

export const normalizeCmsGalleryAlbumsForLegacyView = (albums = []) => {
  return albums.map(normalizeCmsGalleryAlbumForLegacyView).filter(Boolean);
};

const formatDateForLegacyErasmus = (value) => formatDateForLegacyNews(value);

const buildErasmusInstitution = (call) => {
  return (
    call?.institutionName ||
    call?.partner?.name ||
    [call?.city, call?.country].filter(Boolean).join(', ') ||
    'Erasmus+ partner'
  );
};

const getCmsErasmusLogoCandidates = (call) => {
  return [
    ...getCmsMediaCandidateUrls(call?.logoImage, ['medium', 'thumb', 'original']),
    '/assets/img/erasmuskonkursi/adriatiklogo.png',
  ].filter(Boolean);
};

const getCmsErasmusLogo = (call) => {
  return getCmsErasmusLogoCandidates(call)[0] || '/assets/img/erasmuskonkursi/adriatiklogo.png';
};

const getCmsErasmusDocumentUrl = (document) => {
  const candidates = [
    document?.absoluteUrl,
    document?.url,
    document?.downloadUrl,
    document?.media?.absoluteUrl,
    document?.media?.url,
    document?.staticAbsoluteUrl,
    document?.staticUrl,
  ];

  for (const candidate of candidates) {
    const resolved = resolveCmsAssetUrl(candidate || '');
    if (resolved) return resolved;
  }

  return '';
};

const getCmsErasmusDocumentTitle = (document) => {
  return document?.title || document?.media?.originalName || document?.documentType || 'Dokument';
};

export const normalizeCmsErasmusCallForLegacyView = (call) => {
  if (!call) return null;

  const documents = Array.isArray(call.documents) ? call.documents : [];
  const contentHtml = resolveCmsHtmlAssetUrls(call.contentHtml || '');
  const textPreview = stripHtml(contentHtml || call.summary || call.seo?.description || '');
  const legacyId = call.legacy?.id || `cms-erasmus-${call.id}`;
  const slug = call.slug || '';
  const logoCandidates = getCmsErasmusLogoCandidates(call);
  const seoImageCandidates = uniqueResolvedUrls([
    ...getCmsMediaCandidateUrls(call?.ogImage, ['medium', 'thumb', 'original']),
    typeof call?.seo?.image === 'string' ? call.seo.image : '',
    call?.seo?.image?.absoluteUrl,
    call?.seo?.image?.url,
    ...logoCandidates,
  ]);

  return {
    id: legacyId,
    cmsId: call.id,
    source: 'cms',
    slug,
    routePath: call.routePath || (slug ? `/erasmus-konkursi?konkurs=${encodeURIComponent(slug)}` : '/erasmus-konkursi'),
    datum: formatDateForLegacyErasmus(call.publishedAt),
    type: call.callType || 'javniPoziv',
    logo: logoCandidates[0] || getCmsErasmusLogo(call),
    logoCandidates,
    ustanova: buildErasmusInstitution(call),
    naslov: call.title || 'Erasmus+ konkurs',
    seoTitle: call.seo?.title || call.title || 'Erasmus+ konkurs',
    seoDescription: call.seo?.description || call.summary || textPreview,
    ogImage: seoImageCandidates[0] || logoCandidates[0] || getCmsErasmusLogo(call),
    opis: call.summary || call.seo?.description || textPreview,
    tekst: textPreview,
    contentHtml,
    rok: formatDateForLegacyErasmus(call.deadlineAt),
    publishedAt: call.publishedAt || null,
    updatedAt: call.updatedAt || call.publishedAt || null,
    destinacija: [call.city, call.country].filter(Boolean).join(', '),
    callType: call.callType || '',
    mobilityType: call.mobilityType || '',
    targetGroup: call.targetGroup || '',
    applicationUrl: call.applicationUrl || '',
    documentCount: Number(call.documentCount || documents.length || 0),
    detailsLoaded: Boolean(call.contentHtml || documents.length),
    hasMoreInfo: Boolean(call.contentHtml || textPreview || documents.length || call.documentCount || call.applicationUrl),
    dokumenti: [
      ...documents
        .map((document) => ({
          naziv: getCmsErasmusDocumentTitle(document),
          url: getCmsErasmusDocumentUrl(document),
          type: document?.documentType || document?.media?.extension || '',
        }))
        .filter((document) => document.url),
      ...(call.applicationUrl
        ? [{ naziv: 'Online prijava / više informacija', url: call.applicationUrl, type: 'link' }]
        : []),
    ],
    cms: call,
  };
};

export const normalizeCmsErasmusCallsForLegacyView = (calls = []) => {
  return calls.map(normalizeCmsErasmusCallForLegacyView).filter(Boolean);
};

export const normalizeCmsPostsForLegacyView = (posts = []) => {
  return posts.map(normalizeCmsPostForLegacyView).filter(Boolean);
};

export const extractNewsSlugFromRouteParam = (routeParam = '') => {
  const value = String(routeParam || '').trim();
  const legacyMatch = value.match(/^\d+-(.+)$/);

  if (legacyMatch) return legacyMatch[1];
  return value;
};

const cmsClient = {
  config: cmsConfig,
  request: cmsRequest,
  fetchNews: fetchCmsNews,
  fetchNewsBySlug: fetchCmsNewsBySlug,
  fetchCategories: fetchCmsCategories,
  fetchLatestNews: fetchLatestCmsNews,
  fetchFeaturedNews: fetchFeaturedCmsNews,
  fetchGalleryAlbums: fetchCmsGalleryAlbums,
  fetchGalleryAlbumBySlug: fetchCmsGalleryAlbumBySlug,
  fetchGalleryCategories: fetchCmsGalleryCategories,
  fetchErasmusCalls: fetchCmsErasmusCalls,
  fetchErasmusCallBySlug: fetchCmsErasmusCallBySlug,
  fetchErasmusPartners: fetchCmsErasmusPartners,
  normalizePostForLegacyView: normalizeCmsPostForLegacyView,
  normalizePostsForLegacyView: normalizeCmsPostsForLegacyView,
  normalizeGalleryAlbumForLegacyView: normalizeCmsGalleryAlbumForLegacyView,
  normalizeGalleryAlbumsForLegacyView: normalizeCmsGalleryAlbumsForLegacyView,
  normalizeErasmusCallForLegacyView: normalizeCmsErasmusCallForLegacyView,
  normalizeErasmusCallsForLegacyView: normalizeCmsErasmusCallsForLegacyView,
  getPostPath: getCmsPostPath,
  getMediaUrl: getCmsMediaUrl,
  getMediaCandidateUrls: getCmsMediaCandidateUrls,
  resolveAssetUrl: resolveCmsAssetUrl,
  resolveHtmlAssetUrls: resolveCmsHtmlAssetUrls,
  extractNewsSlugFromRouteParam,
};

export default cmsClient;
